import { Component, inject, OnInit } from '@angular/core';
// Was (2026-10-30): import { registerScreenPanels } from '../../studio-nav/screen-panels.service';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { FunctionService } from '../../services/function.service';
import { ClientService } from '../../services/client.service';

interface SchemaField {
  name: string;
  type: string;
  filterable: boolean;
  sortable: boolean;
  groupable: boolean;
  operators?: string[];
  sampleValues?: string[];
  referencesTable?: string;
}

interface SchemaTable {
  name: string;
  displayName: string;
  fieldCount: number;
  fields: SchemaField[];
  suggestedQuestions: string[];
}

interface SchemaRelationship {
  from: string;
  to: string;
  fromTable: string;
  toTable: string;
  via: string;
  type: 'many-to-one' | 'one-to-many';
}

interface SchemaGuide {
  tables: SchemaTable[];
  relationships: SchemaRelationship[];
}

type MainView = 'table' | 'relationships';

@Component({
  selector: 'app-schema-explorer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex h-screen w-screen bg-gray-50 overflow-hidden">

      <!-- SIDEBAR -->
      <div class="w-72 shrink-0 flex flex-col bg-white border-r border-gray-200 overflow-hidden">

        <!-- Header -->
        <div class="shrink-0 px-4 py-4 border-b border-gray-200">
          <button (click)="goBack()" class="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
            Back to Query
          </button>
          <p class="text-base font-semibold text-gray-800">Data Explorer</p>
          <p class="text-xs text-gray-400 mt-0.5">
            {{ loading ? 'Loading...' : (guide ? guide.tables.length + ' tables available' : '') }}
          </p>
        </div>

        <!-- Loading skeleton -->
        <div *ngIf="loading" class="flex-grow p-4 space-y-2">
          <div *ngFor="let i of [1,2,3,4]" class="h-10 rounded-lg bg-gray-100 animate-pulse"></div>
        </div>

        <!-- Nav list -->
        <div *ngIf="!loading && guide" class="flex-grow overflow-y-auto py-2">

          <!-- Relationships -->
          <button *ngIf="guide.relationships.length > 0"
            (click)="mainView = 'relationships'; selectedTable = null"
            [ngClass]="mainView === 'relationships' ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600' : 'text-gray-600 hover:bg-gray-50'"
            class="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors text-left">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
            </svg>
            Relationships
            <span class="ml-auto text-xs px-1.5 py-0.5 rounded-full"
              [ngClass]="mainView === 'relationships' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'">
              {{ guide.relationships.length }}
            </span>
          </button>

          <div class="px-4 pt-3 pb-1">
            <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tables</p>
          </div>

          <button *ngFor="let table of guide.tables"
            (click)="selectTable(table)"
            [ngClass]="selectedTable?.name === table.name ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600' : 'text-gray-600 hover:bg-gray-50'"
            class="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clip-rule="evenodd" />
            </svg>
            <span class="flex-1 truncate font-medium">{{ table.displayName }}</span>
            <span class="text-[10px] text-gray-400 shrink-0">{{ table.fieldCount }}f</span>
          </button>
        </div>
      </div>

      <!-- MAIN CONTENT -->
      <div class="flex-grow flex flex-col overflow-hidden">

        <!-- Loading full page -->
        <div *ngIf="loading" class="flex-grow flex flex-col items-center justify-center gap-4 text-gray-400">
          <svg class="animate-spin h-10 w-10 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm">Analysing your data schema...</p>
          <p class="text-xs text-gray-300">Fetching tables, fields and generating example questions</p>
        </div>

        <!-- Error -->
        <div *ngIf="error && !loading" class="flex-grow flex items-center justify-center">
          <div class="text-center">
            <p class="text-sm font-medium text-red-500">Failed to load schema</p>
            <p class="text-xs mt-1 text-gray-400">{{ error }}</p>
            <button (click)="load()" class="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">Retry</button>
          </div>
        </div>

        <!-- Empty state — no table selected -->
        <div *ngIf="!loading && !error && guide && !selectedTable && mainView !== 'relationships'"
          class="flex-grow flex flex-col items-center justify-center gap-3 text-gray-400 p-8">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582 4 8 4s8 1.79 8 4" />
          </svg>
          <p class="text-sm font-medium text-gray-500">Select a table from the sidebar</p>
          <p class="text-xs text-gray-400">to see its fields, filters, and suggested questions</p>
        </div>

        <!-- TABLE DETAIL -->
        <div *ngIf="!loading && !error && guide && selectedTable && mainView === 'table'" class="flex-grow overflow-y-auto">

          <!-- Table header -->
          <div class="sticky top-0 bg-white border-b border-gray-200 px-8 py-5 z-10">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-xl font-bold text-gray-800">{{ selectedTable.displayName }}</h1>
                <div class="flex flex-wrap gap-1.5 mt-2">
                  <span *ngFor="let badge of getTypeBadges(selectedTable)"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                    [ngClass]="badge.class">
                    {{ badge.label }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="px-8 py-6 grid grid-cols-2 gap-8 max-w-6xl">

            <!-- Suggested questions -->
            <div class="col-span-2">
              <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Questions you can ask</h2>
              <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                <button *ngFor="let q of selectedTable.suggestedQuestions"
                  (click)="askQuestion(q)"
                  class="text-left px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-all text-sm text-gray-600 flex items-start gap-2 group">
                  <span class="text-base leading-tight shrink-0">💬</span>
                  <span class="group-hover:text-indigo-700">{{ q }}</span>
                </button>
              </div>
            </div>

            <!-- Date fields -->
            <div *ngIf="getFieldsByType(selectedTable, 'date').length > 0">
              <h2 class="text-sm font-semibold text-blue-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>📅</span> Date Fields
                <span class="text-xs font-normal text-blue-400 normal-case tracking-normal">filter · sort · group by day/week/month</span>
              </h2>
              <div class="rounded-xl border border-blue-100 overflow-hidden">
                <div *ngFor="let f of getFieldsByType(selectedTable, 'date'); let last = last"
                  [class.border-b]="!last" class="flex items-center gap-4 px-4 py-3 bg-blue-50 border-blue-100">
                  <p class="text-sm font-medium text-gray-700 w-44 shrink-0">{{ f.name }}</p>
                  <p class="text-xs text-blue-500 flex-1">{{ f.operators?.join(' · ') }}</p>
                  <div class="flex gap-1 shrink-0">
                    <span *ngIf="f.filterable" class="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">filter</span>
                    <span *ngIf="f.sortable" class="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">sort</span>
                    <span *ngIf="f.groupable" class="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">group</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Enum/categorical fields -->
            <div *ngIf="getFieldsByType(selectedTable, 'enum').length > 0">
              <h2 class="text-sm font-semibold text-purple-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>🏷</span> Category Fields
                <span class="text-xs font-normal text-purple-400 normal-case tracking-normal">filter by value · group · breakdown</span>
              </h2>
              <div class="rounded-xl border border-purple-100 overflow-hidden">
                <div *ngFor="let f of getFieldsByType(selectedTable, 'enum'); let last = last"
                  [class.border-b]="!last" class="px-4 py-3 bg-purple-50 border-purple-100">
                  <div class="flex items-center gap-4">
                    <p class="text-sm font-medium text-gray-700 w-44 shrink-0">{{ f.name }}</p>
                    <div class="flex flex-wrap gap-1 flex-1">
                      <span *ngFor="let v of f.sampleValues"
                        class="text-xs px-2 py-0.5 rounded-full bg-white border border-purple-200 text-purple-700">{{ v }}</span>
                    </div>
                    <div class="flex gap-1 shrink-0">
                      <span *ngIf="f.filterable" class="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-600 font-medium">filter</span>
                      <span *ngIf="f.groupable" class="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-600 font-medium">group</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Number fields -->
            <div *ngIf="getFieldsByType(selectedTable, 'number').length > 0">
              <h2 class="text-sm font-semibold text-green-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>🔢</span> Number Fields
                <span class="text-xs font-normal text-green-400 normal-case tracking-normal">filter · sum · average · compare</span>
              </h2>
              <div class="rounded-xl border border-green-100 overflow-hidden">
                <div *ngFor="let f of getFieldsByType(selectedTable, 'number'); let last = last"
                  [class.border-b]="!last" class="flex items-center gap-4 px-4 py-3 bg-green-50 border-green-100">
                  <p class="text-sm font-medium text-gray-700 w-44 shrink-0">{{ f.name }}</p>
                  <p class="text-xs text-green-500 flex-1">{{ f.operators?.join(' · ') }}</p>
                  <div class="flex gap-1 shrink-0">
                    <span class="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-600 font-medium">filter</span>
                    <span class="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-600 font-medium">sum</span>
                    <span class="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-600 font-medium">avg</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Text fields -->
            <div *ngIf="getFieldsByType(selectedTable, 'text').length > 0">
              <h2 class="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>📝</span> Text Fields
                <span class="text-xs font-normal text-gray-400 normal-case tracking-normal">filter · search · sort</span>
              </h2>
              <div class="rounded-xl border border-gray-200 overflow-hidden">
                <div *ngFor="let f of getFieldsByType(selectedTable, 'text'); let last = last"
                  [class.border-b]="!last" class="flex items-center gap-4 px-4 py-3 bg-gray-50 border-gray-100">
                  <p class="text-sm font-medium text-gray-700 w-44 shrink-0">{{ f.name }}</p>
                  <div class="flex flex-wrap gap-1 flex-1">
                    <span *ngFor="let v of (f.sampleValues || [])"
                      class="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500">{{ v }}</span>
                  </div>
                  <div class="flex gap-1 shrink-0">
                    <span *ngIf="f.filterable" class="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-500 font-medium">filter</span>
                    <span *ngIf="f.sortable" class="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-500 font-medium">sort</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Boolean fields -->
            <div *ngIf="getFieldsByType(selectedTable, 'boolean').length > 0">
              <h2 class="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>✓</span> Yes / No Fields
              </h2>
              <div class="rounded-xl border border-yellow-100 overflow-hidden">
                <div *ngFor="let f of getFieldsByType(selectedTable, 'boolean'); let last = last"
                  [class.border-b]="!last" class="flex items-center gap-4 px-4 py-3 bg-yellow-50 border-yellow-100">
                  <p class="text-sm font-medium text-gray-700 w-44 shrink-0">{{ f.name }}</p>
                  <p class="text-xs text-yellow-500 flex-1">true / false</p>
                  <span class="text-[10px] px-2 py-0.5 rounded bg-yellow-100 text-yellow-600 font-medium">filter</span>
                </div>
              </div>
            </div>

          </div>
          <div class="h-8"></div>
        </div>

        <!-- RELATIONSHIPS VIEW -->
        <div *ngIf="!loading && !error && guide && mainView === 'relationships'" class="flex-grow overflow-y-auto">

          <div class="sticky top-0 bg-white border-b border-gray-200 px-8 py-5 z-10">
            <h1 class="text-xl font-bold text-gray-800">Table Relationships</h1>
            <p class="text-sm text-gray-400 mt-1">How your tables connect to each other</p>
          </div>

          <div class="px-8 py-6 max-w-4xl">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div *ngFor="let rel of guide.relationships"
                class="rounded-xl border border-gray-200 bg-white p-5 hover:border-indigo-200 hover:shadow-sm transition-all">
                <div class="flex items-center gap-3">
                  <div class="px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
                    <p class="text-sm font-semibold text-indigo-700">{{ rel.from }}</p>
                  </div>
                  <div class="flex-1 flex flex-col items-center">
                    <div class="flex items-center gap-1 w-full">
                      <div class="flex-1 h-px bg-gray-300"></div>
                      <span class="text-xs font-medium text-gray-500 px-1 bg-white">{{ rel.type === 'many-to-one' ? 'N → 1' : '1 → N' }}</span>
                      <div class="flex-1 h-px bg-gray-300"></div>
                    </div>
                  </div>
                  <div class="px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-100">
                    <p class="text-sm font-semibold text-purple-700">{{ rel.to }}</p>
                  </div>
                </div>
                <div class="mt-3 flex items-center gap-2">
                  <span class="text-xs text-gray-400">via</span>
                  <code class="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{{ rel.via }}</code>
                </div>
                <p class="text-xs text-gray-400 mt-2">
                  {{ rel.type === 'many-to-one'
                    ? 'Many ' + rel.from + ' records link to one ' + rel.to + ' record'
                    : 'One ' + rel.from + ' record links to many ' + rel.to + ' records' }}
                </p>
              </div>
            </div>
          </div>
          <div class="h-8"></div>
        </div>

      </div>
    </div>
  `,
})
export class SchemaExplorerComponent implements OnInit {
  private functionService = inject(FunctionService);
  private clientService = inject(ClientService);
  private router = inject(Router);

  guide: SchemaGuide | null = null;
  loading = true;
  error = '';
  mainView: MainView = 'table';
  selectedTable: SchemaTable | null = null;
  // Was (2026-10-30), until Claude stopped reaching this screen:
  // private panels = registerScreenPanels({
  //   screen: 'schema',
  //   here: () => ({ tab: this.mainView === 'relationships' ? 'relationships' : 'tables' }),
  //   holds: () => ({ tables: (this.guide?.tables ?? []).map((t) => t.name), selectedTable: this.selectedTable?.name ?? '' }),
  //   select: (name) => {
  //     if (!this.guide) return false;
  //     if (name === 'relationships') {
  //       this.mainView = 'relationships';
  //       this.selectedTable = null;
  //     } else {
  //       this.mainView = 'table';
  //       this.selectedTable = this.selectedTable ?? this.guide.tables[0] ?? null;
  //     }
  //     return true;
  //   },
  // });

  ngOnInit() { this.load(); }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      this.guide = await this.functionService.call('getSchemaGuide', {
        clientId: this.clientService.selectedClientId,
      }) as SchemaGuide;
      if (this.guide?.tables?.length) {
        this.selectedTable = this.guide.tables[0];
        this.mainView = 'table';
      }
    } catch (e: any) {
      this.error = e?.message || 'Unknown error';
    } finally {
      this.loading = false;
    }
  }

  goBack() { this.router.navigate(['/query']); }

  selectTable(table: SchemaTable) {
    this.selectedTable = table;
    this.mainView = 'table';
  }

  askQuestion(q: string) {
    this.router.navigate(['/query'], { queryParams: { ask: q } });
  }

  getTypeBadges(table: SchemaTable) {
    const counts: Record<string, number> = {};
    for (const f of table.fields) counts[f.type] = (counts[f.type] || 0) + 1;
    const map: Record<string, { label: string; class: string }> = {
      date:    { label: '📅 Dates',       class: 'bg-blue-50 text-blue-600 border border-blue-100' },
      enum:    { label: '🏷 Categories',  class: 'bg-purple-50 text-purple-600 border border-purple-100' },
      number:  { label: '🔢 Numbers',     class: 'bg-green-50 text-green-600 border border-green-100' },
      text:    { label: '📝 Text',        class: 'bg-gray-50 text-gray-500 border border-gray-200' },
      boolean: { label: '✓ Yes/No',       class: 'bg-yellow-50 text-yellow-600 border border-yellow-100' },
    };
    return Object.entries(counts)
      .filter(([type]) => type !== 'id' && type !== 'json' && map[type])
      .map(([type, count]) => ({ ...map[type], label: `${map[type].label} (${count})` }));
  }

  getFieldsByType(table: SchemaTable, type: string): SchemaField[] {
    return table.fields.filter(f => f.type === type);
  }
}
